import { Card as MuiCard, CardProps as MuiCardProps } from "@mui/material";
import { SxProps } from "@mui/system";
import React from "react";

import { borderRadius as borderRadiusMap } from "../../theme/borderRadius";
import { multiplySpacing } from "../../theme/grid";
import { primaryShadows, shadows } from "../../theme/shadows";
import Box from "../Box/Box";

export interface OsCardProps
  extends Partial<
    Pick<
      MuiCardProps,
      | "onMouseEnter"
      | "onMouseLeave"
      | "onFocus"
      | "onBlur"
      | "children"
      | "onClick"
    >
  > {
  /**
   * If true, adds elevation (shadow) on hover for interactivity.
   * @default true
   */
  interactive?: boolean;
  /**
   * Shadow color variant for interactive hover effects.
   * "neutral" - Grey shadows (default)
   * "primary" - Primary-colored shadows for link-like elements
   * @default "neutral"
   */
  shadowVariant?: "neutral" | "primary";
  /**
   * If true, adds a 1px border.
   * @default false
   */
  outlined?: boolean;
  /**
   * If true, shows the card in a selected state with primary-colored border and light background.
   * @default false
   */
  selected?: boolean;
  /**
   * Padding size for the card content.
   * Each unit represents 4px (GRID_SIZE). For example, padding={4} = 16px, padding={8} = 32px.
   * @default 4
   */
  padding?: number;
  /**
   * Border radius size for the card.
   * "small" - 8px (small rounding)
   * "medium" - 16px (medium rounding for cards and containers)
   * "large" - 24px (large rounding for prominent surfaces)
   * @default "medium"
   */
  borderRadius?: "small" | "medium" | "large";
  /**
   * Ref to a link element within the card. When provided, clicking the card will
   * programmatically click the linked element, making the entire card clickable.
   * Useful for creating clickable cards while maintaining accessibility.
   */
  linkRef?: React.RefObject<HTMLAnchorElement>;
}

/**
 * Interactive card component for representing entities in the app (e.g., classes, activities, user profiles).
 * Supports elevation (shadow) on hover when interactive.
 *
 * For static layout containers without interactivity, use the Paper component instead.
 *
 * @example
 * Basic card with content:
 * ```tsx
 * <OsCard padding={8}>
 *   <OsCardMedia image="/path/to/image.jpg" alt="Description" height="200px" />
 *   <Typography variant="h3">Card with Image</Typography>
 * </OsCard>
 * ```
 *
 * @example
 * Card with linkRef for clickable navigation:
 * ```tsx
 * const linkRef = React.useRef<HTMLAnchorElement>(null);
 * <OsCard linkRef={linkRef} padding={8}>
 *   <Typography variant="h3">
 *     <a ref={linkRef} href="/article">Article Title</a>
 *   </Typography>
 *   <Typography>Description</Typography>
 * </OsCard>
 * ```
 */
export const OsCard = ({
  children,
  interactive = true,
  shadowVariant = "neutral",
  outlined = false,
  selected = false,
  padding = 4,
  borderRadius = "medium",
  linkRef,
  onClick,
  ...rest
}: OsCardProps) => {
  // Redirect link clicks (see https://inclusive-components.design/cards/)
  // TODO: Should this also be handling onFocus, onBlur, etc. events?
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (linkRef?.current) {
      const isTextSelected = window.getSelection()?.toString();
      // Only redirect if the text is not selected and the target is not the link
      if (!isTextSelected && linkRef.current !== event.target) {
        linkRef.current.click();
        event.stopPropagation();
        return;
      }
    }
    onClick?.(event);
  };
  const baseStyles: SxProps = {
    borderRadius: borderRadiusMap[borderRadius],
    padding: multiplySpacing(padding),
    overflow: "hidden",
    transition: ["all ease-in-out 0.05s", "outline 0s"].join(", "),
    outlineColor: "transparent",
    outlineWidth: "2px",
    outlineStyle: "solid",
    outlineOffset: "2px",
  };

  const variantStyles: SxProps = {};

  if (interactive) {
    variantStyles.cursor = "pointer";

    if (shadowVariant === "primary") {
      variantStyles["&:hover"] = {
        boxShadow: primaryShadows[100],
      };

      variantStyles["&:active"] = {
        boxShadow: primaryShadows[50],
      };
    } else {
      variantStyles["&:hover"] = {
        boxShadow: shadows[100],
      };

      variantStyles["&:active"] = {
        boxShadow: shadows[50],
      };
    }
  }

  if (selected) {
    variantStyles.border = "1px solid";
    variantStyles.borderColor = "primary.main";
    variantStyles.backgroundColor = "primary.50";

    variantStyles["&:hover"] = {
      ...variantStyles["&:hover"],
      borderColor: "primary.800",
    };

    variantStyles["&:active"] = {
      ...variantStyles["&:active"],
      borderColor: "primary.900",
    };
  } else if (outlined) {
    variantStyles.border = "1px solid";
    variantStyles.borderColor = "grey.200";

    variantStyles["&:hover"] = {
      ...variantStyles["&:hover"],
      borderColor: "grey.300",
    };

    variantStyles["&:active"] = {
      ...variantStyles["&:active"],
      borderColor: "grey.400",
    };
  }

  return (
    <MuiCard
      {...rest}
      onClick={handleClick}
      sx={{
        ...baseStyles,
        ...variantStyles,
      }}
    >
      {children}
    </MuiCard>
  );
};

export interface OsCardMediaProps {
  /**
   * The image URL to display.
   */
  image: string;
  /**
   * Alt text for the image (required for accessibility).
   */
  alt: string;
  /**
   * Height of the image.
   * @default "14rem"
   * @example "200px", "14rem", "100%"
   */
  height?: string | number;
  /**
   * Width of the image.
   * @default "100%"
   * @example "200px", "14rem", "100%"
   */
  width?: string | number;
  /**
   * Interactive effect to apply on hover.
   * "zoom" - Zooms in the image when parent card is hovered
   */
  interactiveEffect?: "zoom";
  /**
   * Border radius size for the media.
   * "small" - 8px (small rounding)
   * "medium" - 16px (medium rounding for cards and containers)
   * "large" - 24px (large rounding for prominent surfaces)
   * @default "small"
   */
  borderRadius?: "small" | "medium" | "large";
  /**
   * Optional children to render on top of the background image.
   */
  children?: React.ReactNode;
}

/**
 * OsCardMedia displays images that reach the bounds of the card.
 * Uses a background-image approach for better zoom animations.
 * Based on MUI's CardMedia component with Outschool styling.
 *
 * @example
 * ```tsx
 * <OsCard interactive padding={8}>
 *   <OsCardMedia
 *     image="/path/to/image.jpg"
 *     alt="Description of image"
 *     height="200px"
 *     interactiveEffect="zoom"
 *   />
 *   <Typography variant="h3">Card with Image</Typography>
 * </OsCard>
 * ```
 */
export const OsCardMedia = ({
  image,
  alt,
  height = "14rem",
  width = "100%",
  interactiveEffect,
  borderRadius = "small",
  children,
}: OsCardMediaProps) => {
  const mediaStyles: SxProps = {
    height,
    width,
    backgroundImage: `url(${image})`,
    backgroundSize: "100%",
    backgroundPosition: "center",
    transition: "background-size 0.05s ease-in-out",
    position: "relative",
    borderRadius: borderRadiusMap[borderRadius],
  };

  const interactiveStyles: SxProps = {};

  if (interactiveEffect === "zoom") {
    interactiveStyles[".MuiCard-root:hover &"] = {
      backgroundSize: "105%",
    };
  }

  return (
    <Box
      role="img"
      aria-label={alt}
      sx={{
        ...mediaStyles,
        ...interactiveStyles,
      }}
    >
      {children}
    </Box>
  );
};
